import type { DragEndEvent, DragMoveEvent, DragStartEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Box } from '@mui/material';
import { format, isToday } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { DropGhost } from '../components/DropGhost';
import { EventTile } from '../components/EventTile';
import { CHEMISTRY_HATCH, COACHING_HATCH } from '../components/LegendBar';
import type {
  AvailableRanges,
  CalendarEvent,
  Conflict,
  DropProposal,
  Hour,
  ISODate,
  Member,
} from '../types';
import {
  dateEquals,
  findConflict,
  layoutDayEvents,
  parseISODate,
  snapHourFromOffset,
  todayISO,
  unavailableRanges,
  weekDates,
} from '../utils';

const DEFAULT_HOURS = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23,
];
const SLOT_HEIGHT = 48;
const TIME_COL_WIDTH = 56;
const DAY_HEADER_HEIGHT = 50;

type GhostState = {
  dayIdx: number;
  start: Hour;
  duration: Hour;
  conflict: Conflict | null;
};

type WeekViewProps = {
  cursor: ISODate;
  events: CalendarEvent[];
  members?: Member[];
  selectedEventId?: string | null;
  selectedMemberIds?: string[] | null;
  weekStartsOn?: 0 | 1;
  hours?: number[];
  slotHeight?: number;
  snapMinutes?: 15 | 30 | 60;
  canDragEvent?: (event: CalendarEvent) => boolean;
  onEventClick?: (event: CalendarEvent) => void;
  onEmptySlotClick?: (date: ISODate, start: number) => void;
  onEventDrop?: (proposal: DropProposal) => boolean | undefined | Promise<boolean | undefined>;
  onEventDragStart?: (eventId: string) => void;
  onEventDragEnd?: () => void;
  availableRanges?: AvailableRanges;
  chemistryAvailableRanges?: AvailableRanges;
};

function defaultCanDrag(event: CalendarEvent) {
  return event.kind === 'session';
}

function formatHourLabel(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

// ---- Draggable tile wrapper ----

function DraggableEventTile({
  event,
  canDrag,
  ...tileProps
}: {
  event: CalendarEvent;
  canDrag: boolean;
} & Omit<
  React.ComponentProps<typeof EventTile>,
  'dragRef' | 'dragListeners' | 'dragAttributes' | 'isDragging'
>) {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: event.id,
    data: { event },
    disabled: !canDrag,
  });

  return (
    <EventTile
      {...tileProps}
      event={event}
      isDragging={isDragging}
      dragRef={setNodeRef}
      dragListeners={listeners as Record<string, (...args: unknown[]) => unknown>}
      dragAttributes={
        attributes as unknown as Record<string, string | boolean | number | undefined>
      }
    />
  );
}

// ---- Droppable day column ----

function DroppableDayColumn({
  date,
  children,
  isLast,
  height,
}: {
  date: ISODate;
  children: React.ReactNode;
  isLast: boolean;
  height: number;
}) {
  const { setNodeRef } = useDroppable({ id: date });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        borderRight: isLast ? 'none' : '1px solid #f0ede8',
        background: '#fff',
        position: 'relative',
        height,
      }}
    >
      {children}
    </Box>
  );
}

// ---- WeekView ----

export function WeekView({
  cursor,
  events,
  members = [],
  selectedEventId,
  selectedMemberIds,
  weekStartsOn = 1,
  hours = DEFAULT_HOURS,
  slotHeight = SLOT_HEIGHT,
  snapMinutes = 15,
  canDragEvent = defaultCanDrag,
  onEventClick,
  onEmptySlotClick,
  onEventDrop,
  onEventDragStart,
  onEventDragEnd,
  availableRanges,
  chemistryAvailableRanges,
}: WeekViewProps) {
  const days = weekDates(cursor, weekStartsOn);
  const baseHour = hours[0];
  const gridHeight = hours.length * slotHeight;
  const memberMap = new Map(members.map((m) => [m.id, m]));

  // Scroll container ref — used to auto-scroll to morning on mount
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Scroll to 8am on first render so mornings are visible by default
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only scroll to initial position
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const INITIAL_HOUR = 8;
    el.scrollTop = Math.max(0, (INITIAL_HOUR - hours[0]) * slotHeight);
  }, []);

  // Track mouse position for ghost snap calculation
  const mouseYRef = useRef(0);
  // Y offset from mouse to the top of the grabbed tile at drag start
  const grabOffsetYRef = useRef(0);
  // Stable reference to the dragging event's data (avoids stale closures in handlers)
  const activeDragRef = useRef<{
    eventId: string;
    duration: Hour;
    originalDate: ISODate;
    originalStart: Hour;
  } | null>(null);

  const [ghostState, setGhostState] = useState<GhostState | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function isVisible(ev: CalendarEvent): boolean {
    if (ev.kind !== 'session') return true;
    if (!selectedMemberIds) return true;
    if (!ev.memberId) return true;
    return selectedMemberIds.includes(ev.memberId);
  }

  function handleDragStart({ active }: DragStartEvent) {
    const event = active.data.current?.event as CalendarEvent | undefined;
    if (!event) return;

    // Grab offset: how far below the tile's top was the mouse when they clicked?
    const tileTop = active.rect.current.initial?.top ?? mouseYRef.current;
    grabOffsetYRef.current = mouseYRef.current - tileTop;

    activeDragRef.current = {
      eventId: event.id,
      duration: event.end - event.start,
      originalDate: event.date,
      originalStart: event.start,
    };

    onEventDragStart?.(event.id);
  }

  function handleDragMove({ over }: DragMoveEvent) {
    const drag = activeDragRef.current;
    if (!over || !drag) {
      setGhostState(null);
      return;
    }

    const dayIdx = days.indexOf(over.id as string);
    if (dayIdx === -1) {
      setGhostState(null);
      return;
    }

    // Compute snapped ghost position
    const columnTop = over.rect.top;
    const rawTop = mouseYRef.current - columnTop - grabOffsetYRef.current;
    const clampedTop = Math.max(0, rawTop);

    const minHour = hours[0];
    const maxHour = hours[hours.length - 1] + 1 - drag.duration;
    const snapped = snapHourFromOffset(minHour, clampedTop, slotHeight, snapMinutes);
    const ghostStart = Math.max(minHour, Math.min(maxHour, snapped));

    const conflict = findConflict(
      days[dayIdx],
      ghostStart,
      ghostStart + drag.duration,
      events,
      drag.eventId,
      todayISO(),
      members
    );

    setGhostState({ dayIdx, start: ghostStart, duration: drag.duration, conflict });
  }

  async function handleDragEnd({ over }: DragEndEvent) {
    const drag = activeDragRef.current;
    const ghost = ghostState;

    // Clean up first
    activeDragRef.current = null;
    setGhostState(null);
    onEventDragEnd?.();

    if (!over || !drag || ghost === null) return;

    const dayIdx = days.indexOf(over.id as string);
    if (dayIdx === -1) return;

    const proposal: DropProposal = {
      eventId: drag.eventId,
      fromDate: drag.originalDate,
      fromStart: drag.originalStart,
      toDate: days[dayIdx],
      toStart: ghost.start,
      toEnd: ghost.start + drag.duration,
      conflict: ghost.conflict,
    };

    await onEventDrop?.(proposal);
  }

  function handleDragCancel() {
    activeDragRef.current = null;
    setGhostState(null);
    onEventDragEnd?.();
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          background: '#fafaf8',
        }}
        onPointerMove={(e) => {
          mouseYRef.current = e.clientY;
        }}
      >
        {/* Sticky day header row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `${TIME_COL_WIDTH}px repeat(7, 1fr)`,
            position: 'sticky',
            top: 0,
            zIndex: 4,
            background: '#fff',
            borderBottom: '1px solid #e8e5e0',
            minWidth: 700,
          }}
        >
          <Box sx={{ background: '#fff' }} />

          {days.map((day) => {
            const d = parseISODate(day);
            const todayFlag = isToday(d);
            return (
              <Box
                key={day}
                sx={{
                  padding: '10px 8px',
                  borderRight: '1px solid #f0ede8',
                  background: todayFlag ? '#BDCCBE' : '#fff', // sage-400 highlight for today
                  textAlign: 'center',
                  height: DAY_HEADER_HEIGHT,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    fontSize: 10,
                    color: todayFlag ? '#2a3a22' : '#888',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    letterSpacing: '0.04em',
                  }}
                >
                  {format(d, 'EEE')}
                </Box>
                <Box
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: todayFlag ? '#2a3a22' : '#222',
                    mt: '2px',
                  }}
                >
                  {format(d, 'd')}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Body: time col + droppable day columns */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `${TIME_COL_WIDTH}px repeat(7, 1fr)`,
            flex: 1,
            minWidth: 700,
          }}
        >
          {/* Time column */}
          <Box
            sx={{
              background: '#fff',
              borderRight: '1px solid #e8e5e0',
              position: 'sticky',
              left: 0,
              zIndex: 1,
            }}
          >
            {hours.map((hour) => (
              <Box
                key={hour}
                sx={{
                  height: slotHeight,
                  paddingRight: '6px',
                  paddingTop: '2px',
                  textAlign: 'right',
                  fontSize: 10,
                  color: '#999',
                  borderBottom: '1px solid #f5f5f3',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatHourLabel(hour)}
              </Box>
            ))}
          </Box>

          {/* Day columns */}
          {days.map((day, dayIdx) => {
            const dayEvents = events.filter((ev) => dateEquals(ev.date, day) && isVisible(ev));
            const layouts = layoutDayEvents(dayEvents);
            const dayOfWeek = parseISODate(day).getDay();
            const dayRanges = availableRanges?.[dayOfWeek];
            const hasAvailability = availableRanges !== undefined;
            const showKindHatches = hasAvailability && chemistryAvailableRanges !== undefined;
            const gridStart = hours[0];
            const gridEnd = hours[hours.length - 1] + 1;
            const hatchLayers = showKindHatches
              ? [
                  {
                    kind: 'coaching',
                    background: COACHING_HATCH,
                    gaps: unavailableRanges(dayRanges ?? [], gridStart, gridEnd),
                  },
                  {
                    kind: 'chemistry',
                    background: CHEMISTRY_HATCH,
                    gaps: unavailableRanges(
                      chemistryAvailableRanges?.[dayOfWeek] ?? [],
                      gridStart,
                      gridEnd
                    ),
                  },
                ]
              : [];

            return (
              <DroppableDayColumn key={day} date={day} isLast={dayIdx === 6} height={gridHeight}>
                {/* Availability background. Without chemistryAvailableRanges: legacy grey base with white bands for available windows. With it: per-kind hatch overlays marking unavailable time for coaching and Chem Call. */}
                {showKindHatches && (
                  <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
                    {hatchLayers.flatMap(({ kind, background, gaps }) =>
                      gaps.map((range, i) => (
                        <Box
                          key={`${kind}-${i}`}
                          data-testid={`availability-hatch-${kind}`}
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: (range.start - gridStart) * slotHeight,
                            height: (range.end - range.start) * slotHeight,
                            background,
                            pointerEvents: 'none',
                          }}
                        />
                      ))
                    )}
                  </Box>
                )}
                {hasAvailability && !showKindHatches && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 0,
                      background: '#f0efed',
                      pointerEvents: 'none',
                    }}
                  >
                    {(dayRanges ?? []).map((range, i) => {
                      const top = (range.start - hours[0]) * slotHeight;
                      const height = (range.end - range.start) * slotHeight;
                      return (
                        <Box
                          key={i}
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top,
                            height,
                            background: '#fff',
                          }}
                        />
                      );
                    })}
                  </Box>
                )}

                {/* Hour slots */}
                {hours.map((hour) => (
                  <Box
                    key={hour}
                    sx={{
                      height: slotHeight,
                      borderBottom: '1px solid #f5f5f3',
                      position: 'relative',
                      zIndex: 1,
                      '&:hover': { background: 'rgba(0,0,0,0.01)' },
                    }}
                    onClick={() => onEmptySlotClick?.(day, hour)}
                  />
                ))}

                {/* Event tiles */}
                {layouts.map(({ event: ev, colIdx, numCols }) => {
                  const member = ev.memberId ? memberMap.get(ev.memberId) : undefined;
                  const isDraggable = canDragEvent(ev);
                  return (
                    <DraggableEventTile
                      key={ev.id}
                      event={ev}
                      member={member}
                      baseHour={baseHour}
                      slotHeight={slotHeight}
                      isSelected={ev.id === selectedEventId}
                      hasConflict={Boolean(ev.meta?.conflict)}
                      canDrag={isDraggable}
                      colIdx={colIdx}
                      numCols={numCols}
                      onClick={onEventClick}
                    />
                  );
                })}

                {/* Drop ghost for this column */}
                {ghostState?.dayIdx === dayIdx && ghostState.start !== null && (
                  <DropGhost
                    start={ghostState.start}
                    duration={ghostState.duration}
                    baseHour={baseHour}
                    slotHeight={slotHeight}
                    hasConflict={ghostState.conflict !== null}
                  />
                )}
              </DroppableDayColumn>
            );
          })}
        </Box>
      </Box>
    </DndContext>
  );
}
